import { useEffect, useState } from 'react';

import { differenceInMinutes } from 'date-fns';
import { IntlFormatters, useIntl } from 'react-intl';

import { useGetUserOrdersQuery } from 'generated/graphql-gateway';
import { DeliveryStatus, RbiOrderStatus } from 'generated/rbi-graphql';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useIOSLiveActivities } from 'hooks/use-ios-live-activities';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMatch } from 'state/location';
import { OrderStatus, ServiceMode, useOrderContext } from 'state/order';
import { isOrderTimeInInterval } from 'state/order/utils';
import { useServiceModeContext } from 'state/service-mode';
import { deliveryCompleteTimeout } from 'utils/delivery-complete-timeout';
import { getDeliveryHeading } from 'utils/get-delivery-heading';
import { routes } from 'utils/routing';
import { getServiceModeIcon } from 'utils/service-mode';

import { IOrderInProgressRecentOrder, IOrderStatusBarData } from './types';

const DELIVERY_COMPLETED = [
  DeliveryStatus.ORDER_DROPPED_OFF,
  DeliveryStatus.ORDER_ABANDONED,
  DeliveryStatus.ORDER_CANCELLED,
];

const NOTIFICATION_EXPIRATION_IN_MINUTES = 120;
const ORDER_PROGRESS_POLLING_DURATION_IN_MS = 10000;
const DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES = 10;
const PICKUP_UPDATED_TIMEOUT_IN_MINUTES = 10;

const isPollingForDelivery = ({
  hasDelivery,
  isDeliveryOrderCompleted,
  deliveryStatus,
}: {
  hasDelivery: boolean;
  isDeliveryOrderCompleted: boolean;
  deliveryStatus: DeliveryStatus | undefined;
}) =>
  hasDelivery &&
  !isDeliveryOrderCompleted &&
  (!deliveryStatus || !DELIVERY_COMPLETED.includes(deliveryStatus));

const getStatusBarData = (
  serviceMode: ServiceMode,
  formatMessage: IntlFormatters['formatMessage']
): IOrderStatusBarData | null => {
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      return {
        icon: getServiceModeIcon(serviceMode),
        heading: formatMessage({ id: 'driveThruOrderPlaced' }),
        cta: {
          text: formatMessage({ id: 'seeDetails' }),
        },
      };
    case ServiceMode.TAKEOUT:
      return {
        icon: getServiceModeIcon(serviceMode),
        heading: formatMessage({ id: 'pickUpOrderPlaced' }),
        cta: {
          text: formatMessage({ id: 'seeDetails' }),
        },
      };
    case ServiceMode.EAT_IN:
      return {
        icon: getServiceModeIcon(serviceMode),
        heading: formatMessage({ id: 'dineInOrderPlaced' }),
        cta: {
          text: formatMessage({ id: 'seeDetails' }),
        },
      };
    case ServiceMode.DELIVERY:
      return {
        icon: getServiceModeIcon(serviceMode),
        heading: formatMessage({ id: 'dineInOrderPlaced' }),
        cta: {
          text: formatMessage({ id: 'seeDetails' }),
        },
      };
    case ServiceMode.CURBSIDE:
      return {
        icon: getServiceModeIcon(serviceMode),
        heading: formatMessage({ id: 'dineInOrderPlaced' }),
        cta: {
          text: formatMessage({ id: 'seeDetails' }),
        },
      };
    default:
      return null;
  }
};

export const useTopOrderStatusBar = (): IOrderStatusBarData | null => {
  const fireOrderAhead = useFlag(LaunchDarklyFlag.FIRE_ORDER_AHEAD);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );
  const isTopOrderStatusBarEnabled = useFlag(LaunchDarklyFlag.ENABLE_TOP_ORDER_STATUS_BAR);
  const { formatMessage } = useIntl();
  const { linkTo } = useNavigation();
  const { isAuthenticated } = useAuthContext();
  const { curbsidePickupOrderId, isPreConfirmCurbside, serverOrder } = useOrderContext();
  const { isCurbside } = useServiceModeContext();
  const { updateActivity } = useIOSLiveActivities();
  const [hasSeenCancelModal, setHasSeenCancelModal] = useState(false);
  const [shouldDisplayNotification, setShouldDisplayNotification] = useState<boolean>(true);
  const [currentOrderId, setCurrentOrderId] = useState<string>('');
  const [currentDeliveryStatus, setCurrentDeliveryStatus] = useState<DeliveryStatus>();

  const isOnMenuPage = useMatch({
    path: routes.menu,
    end: false,
  });

  // start polling for recent user orders
  const { data, startPolling, stopPolling, refetch } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL,
      ],
    },
    skip: !isAuthenticated,
  });

  // Upon order confirmation, data from `useGetUseOrdersQuery` is not updated with newly placed order
  // Force a refetch to update query with new confirm order instead of previous order
  const isServerOrderSuccessful: boolean = serverOrder?.status === OrderStatus.INSERT_SUCCESSFUL;
  const isCurbsideOrderCompleted = isCurbside && isServerOrderSuccessful;

  const order: IOrderInProgressRecentOrder =
    isCurbsideOrderCompleted && serverOrder
      ? { ...serverOrder, updatedAt: serverOrder?.createdAt || '' }
      : data?.userOrders?.orders?.[0];

  const statusBarData = getStatusBarData(order?.cart.serviceMode as ServiceMode, formatMessage);

  const isPickupTimedOut = () => {
    // Add the fire order delay to the total timeout
    const fireOrderInSeconds = order?.fireOrderIn ? order?.fireOrderIn + fireOrderAhead : 0;
    const fireOrderInMinutes = fireOrderInSeconds / 60;
    const timeOutWithDelay = PICKUP_UPDATED_TIMEOUT_IN_MINUTES + fireOrderInMinutes;

    return order && !order?.delivery && isOrderTimeInInterval(order?.updatedAt, timeOutWithDelay);
  };

  useEffect(() => {
    if (isCurbsideOrderCompleted) {
      refetch();
    }
  }, [isCurbsideOrderCompleted, refetch]);

  useEffect(() => {
    // Update notification for new orders being placed
    const order = data?.userOrders?.orders?.[0];
    if (order && order.rbiOrderId !== currentOrderId) {
      setShouldDisplayNotification(true);
      setCurrentOrderId(order.rbiOrderId);
    }
  }, [currentOrderId, data, isCurbside]);

  useEffect(() => {
    // Update notification for delivery status changes
    const order = data?.userOrders?.orders?.[0];
    if (order?.delivery?.status && order?.delivery.status !== currentDeliveryStatus) {
      setShouldDisplayNotification(true);
      setCurrentDeliveryStatus(order?.delivery?.status);
    }
  }, [currentDeliveryStatus, data]);

  useEffect(() => {
    // reactivate shouldDisplayNotification after closing first Curbside notification
    if (isServerOrderSuccessful) {
      setShouldDisplayNotification(true);
    }
  }, [isServerOrderSuccessful]);

  if (
    !isTopOrderStatusBarEnabled ||
    isOnMenuPage ||
    !isAuthenticated ||
    !shouldDisplayNotification
  ) {
    return null;
  }

  const isDeliveryOrderCompleted =
    !!order?.delivery &&
    DELIVERY_COMPLETED.includes(order.delivery.status) &&
    (hasSeenCancelModal ||
      deliveryCompleteTimeout(order?.updatedAt, DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES));

  if (
    isPollingForDelivery({
      hasDelivery: !!order?.delivery,
      isDeliveryOrderCompleted,
      deliveryStatus: order?.delivery?.status,
    })
  ) {
    if (hasSeenCancelModal) {
      setHasSeenCancelModal(false);
    }
    startPolling(ORDER_PROGRESS_POLLING_DURATION_IN_MS);
  }

  const orderId = isPreConfirmCurbside() ? curbsidePickupOrderId : currentOrderId;

  // CURBSIDE - Before order is commited on confirming arrival , selecting "I'm here!"
  if (isPreConfirmCurbside()) {
    return {
      ...statusBarData,
      heading: formatMessage({ id: 'curbsideOrderSaved' }),
      icon: getServiceModeIcon(ServiceMode.CURBSIDE),
      cta: {
        action: () => linkTo(`${routes.orderConfirmation}/${orderId}`),
        text: formatMessage({ id: 'tapWhenHere' }),
      },
    };
  }

  // CURBSIDE - After order is placed
  if (isCurbside && isPickupTimedOut()) {
    return {
      ...statusBarData,
      heading: formatMessage({ id: 'curbsideOrderPlaced' }),
      icon: getServiceModeIcon(ServiceMode.CURBSIDE),
      cta: {
        text: formatMessage({ id: 'seeDetails' }),
        action: () => linkTo(`${routes.orderConfirmation}/${orderId}`),
      },
    };
  }

  const notificationIsExpired =
    order?.updatedAt &&
    Math.abs(differenceInMinutes(new Date(), new Date(order.updatedAt))) >
      NOTIFICATION_EXPIRATION_IN_MINUTES;

  if (notificationIsExpired || order?.status === RbiOrderStatus.REFUND_SUCCESSFUL) {
    return null;
  }

  // PICKUP
  if (order && statusBarData && isPickupTimedOut()) {
    // DRIVE_THRU when Mobile Pickup is Enabled
    if (
      order?.cart.serviceMode === ServiceMode.DRIVE_THRU &&
      isMobileOrderForDriveThruLaneEnabled
    ) {
      statusBarData.heading = formatMessage({ id: 'driveThruMobileOrderPlaced' });
    }

    return {
      ...statusBarData,
      icon: getServiceModeIcon(ServiceMode.DRIVE_THRU),
      cta: {
        text: formatMessage({ id: 'seeDetails' }),
        action: () => linkTo(`${routes.orderConfirmation}/${orderId}`),
      },
    };
  }

  // DELIVERY
  if (order?.delivery && !isDeliveryOrderCompleted) {
    const deliveryStatus = order.delivery.status;
    const headingId = getDeliveryHeading({ deliveryStatus });
    if (order.rbiOrderId && deliveryStatus) {
      updateActivity(order, false);
    }
    return {
      ...statusBarData,
      icon: getServiceModeIcon(ServiceMode.DELIVERY),
      heading: formatMessage({ id: headingId }),
      cta: {
        text: formatMessage({ id: 'seeDetails' }),
        action: () => linkTo(`${routes.orderConfirmation}/${orderId}`),
      },
    };
  }

  // Stop polling for recent user orders
  stopPolling();

  return null;
};
